import {
  AgXToneMapping,
  PCFShadowMap,
  PerspectiveCamera,
  SRGBColorSpace,
  WebGPURenderer,
} from 'three/webgpu';
import type { Camera, Object3D, WebGPURendererParameters } from 'three/webgpu';
import { loadSettings } from '../ui/settings.js';

/**
 * Three quality tiers, chosen once at boot, and after the 2026-09-05 pivot they are much
 * closer together than the names suggest:
 *
 * - the low tier — a machine reporting four or fewer logical cores, or an explicit setting —
 *   caps its pixel ratio at 1.25 and is the only tier that skips the material detail layers;
 * - `'pc'` is the default and is the whole render path: one 2048 sun cascade, 4x MSAA in the
 *   scene pass, low bloom, grade;
 * - `'ultra'` is `'pc'` plus opt-in extras and a devicePixelRatio ceiling of 2. It is never
 *   chosen by the heuristic. There is no ultra-only *pass* any more: the shafts, streak,
 *   depth of field and fourth shadow cascade that justified the tier are gone.
 *
 * Where a feature only distinguishes "the low tier or not", test `tier !== 'low'`, never
 * `tier === 'pc'` — the latter silently drops the feature from ultra.
 */
export type QualityTier = 'low' | 'pc' | 'ultra';

/**
 * Object layer for skinned rigs (Hymns, warm-up bodies). The world camera sees it; the sun's
 * shadow camera no longer does at all (render/shadows.ts). Characters cast blob shadows now —
 * a single instanced decal mesh — because a skinned rig re-skinned once per shadow pass was
 * the frame on the owner's 4080 (WEBGPU-NOTES §2, §6). The layer survives as "things the
 * shadow pass must not see".
 */
export const RIG_LAYER = 1;

/**
 * Per-frame hooks run by the animation-loop wrapper below, *before* the frame callback
 * `main.ts` installed. For render modules that need a per-frame step but have no line in
 * `main.ts`'s frame loop (cascaded shadow frusta tracking the camera's FOV, GPU timing). Kept
 * here so the render stack can grow a frame step without editing the file that owns the loop.
 */
const frameHooks: Array<(timeMs: number) => void> = [];
export function onBeforeFrame(fn: (timeMs: number) => void): () => void {
  frameHooks.push(fn);
  return () => {
    const i = frameHooks.indexOf(fn);
    if (i >= 0) frameHooks.splice(i, 1);
  };
}

/**
 * Where dynamic-resolution steps go, if anything wants them. **Nothing registers one today**
 * and that is deliberate: with no sink, DRS scales the canvas pixel ratio, and every render
 * target in `render/post.ts` sizes itself from the drawing buffer each frame, so a DRS step
 * scales the scene pass, the bloom target and the output quad together. The sink existed for
 * TAAU, which wanted the scene pass to shrink while the canvas stayed native — the exact shape
 * that left the post chain running at output resolution (WEBGPU-NOTES §2). Kept because a
 * future upscaler would want it back; see `applyPixelRatio` for the hybrid path it drives.
 */
/**
 * Late-content precompile. Any loader that attaches renderable content *after* the deploy's
 * precompile (a container GLB, a Hymn rig, a loot beam) calls `requestLatePrecompile(label)`;
 * main.ts registers the one hook that knows the renderer/scene/camera/post chain, and the
 * requests coalesce (300 ms) into a single `precompileScene(..., () => post.compileScene())`.
 * Without this the first draw of that content compiles its pipelines synchronously on the main
 * thread — the 2-4 s "shots do not register" hitches on the live build (2026-09-05).
 */
let latePrecompileHook: ((label: string) => void) | null = null;
let lateTimer: ReturnType<typeof setTimeout> | null = null;
const lateLabels = new Set<string>();
export function setLatePrecompileHook(fn: (label: string) => void): void {
  latePrecompileHook = fn;
}
export function requestLatePrecompile(label: string): void {
  lateLabels.add(label);
  if (lateTimer !== null || latePrecompileHook === null) return;
  lateTimer = setTimeout(() => {
    lateTimer = null;
    const label = [...lateLabels].join('+');
    lateLabels.clear();
    latePrecompileHook?.(label);
  }, 300);
}

/**
 * Hooks around every precompile. `'begin'` runs with the loop parked, before
 * `compileAsync`; `'end'` after it (also on failure). render/shadows.ts uses them to widen the
 * cascades to the whole scene for the compile, so every caster's shadow variant is built even
 * when it sits outside the cascade frusta at that moment.
 */
const precompileHooks: Array<(phase: 'begin' | 'end') => void> = [];
export function onPrecompile(fn: (phase: 'begin' | 'end') => void): () => void {
  precompileHooks.push(fn);
  return () => {
    const i = precompileHooks.indexOf(fn);
    if (i >= 0) precompileHooks.splice(i, 1);
  };
}

/**
 * Warm-up bodies (`warmHymnBodies`, `NpcField.warmBodies`) exist so the precompile builds their
 * pipelines; between precompiles they are pure per-pass traversal cost (518 objects, and the
 * NPC copies are unculled skinned draws). They live here and are attached to their scene only
 * for the duration of each `runPrecompile`.
 */
const warmPools: Array<{ scene: Object3D; pool: Object3D }> = [];
export function registerWarmPool(scene: Object3D, pool: Object3D): void {
  warmPools.push({ scene, pool });
}

let drsSink: ((scale: number) => void) | null = null;
export function setDrsSink(fn: (scale: number) => void): () => void {
  drsSink = fn;
  return () => {
    if (drsSink === fn) drsSink = null;
  };
}

export interface RendererHandle {
  renderer: WebGPURenderer;
  camera: PerspectiveCamera;
  /** 'webgpu' | 'webgl2' — whichever backend actually initialised. */
  backend: 'webgpu' | 'webgl2';
  tier: QualityTier;
  /** Notify the renderer that a second (view model) camera needs the same aspect. */
  onResize(fn: (aspect: number) => void): void;
  /** Detaches the resize listener. */
  dispose(): void;
}

/**
 * Live render-resolution readout, kept up to date by the resize/dynamic-resolution code
 * below. The HUD (`ui/hud.ts`) has no reference to the renderer instance — its stats
 * updater is driven entirely from `main.ts` — so this is exported as a module-level
 * object rather than threaded through a constructor param. The same object is also
 * hung off `renderer.stats` for anything reaching for it off the renderer itself.
 */
export interface GfxStats {
  /** CSS (logical) canvas size, in the units `window.innerWidth/Height` report. */
  cssW: number;
  cssH: number;
  /** Actual backing-store size: cssW/H * the effective (capped * DRS) pixel ratio. */
  drawW: number;
  drawH: number;
  /** Dynamic-resolution multiplier currently applied on top of the capped base ratio. */
  scale: number;
  /** Whether MSAA is active. Fixed at renderer construction — WebGPU can't toggle it live. */
  aa: boolean;
  /**
   * Whether the TSL post chain (`render/post.ts`) is running. Written once by `createPost`,
   * which is also where `?post=0` is read; the HUD prints it next to the resolution so a
   * perf A/B is legible from the screen rather than from the console.
   */
  post: boolean;
  /** The resolved quality tier, for the HUD/feedback context. */
  tier: QualityTier;
  /**
   * GPU time of the last resolved frame in ms, from WebGPU timestamp queries, or -1 when they
   * are off (`?gputime=1` turns them on) or unsupported. The one number that separates "the GPU
   * is the bottleneck" from "the main thread is" — rAF frame time alone cannot.
   */
  gpuMs: number;
  /**
   * Temporal AA/upscale mode the post chain settled on: 'taau' when the scene renders under
   * full resolution and is resolved up, 'traa' at 1:1, 'smaa'/'fxaa' for the spatial paths.
   * Written by `render/post.ts`.
   */
  aaMode: 'taau' | 'traa' | 'smaa' | 'fxaa' | 'msaa' | 'none';
  /** Internal render scale the scene pass runs at under TAAU (1 otherwise). */
  renderScale: number;
  /** True while a precompile has the render loop parked (tools wait on this before sampling). */
  precompiling: boolean;
  /**
   * Main-thread time inside the frame callback, EMA in ms. Against the rAF delta this is the
   * CPU-bound/GPU-bound split without timestamp queries: on the owner's 4080 (2026-09-05) the
   * frame was 80-97 ms with the GPU at 36% because this number *was* the frame.
   */
  cpuMs: number;
  /** Draw calls in the last frame, every pass included (three's `info.render.drawCalls`). */
  draws: number;
}

export const gfxStats: GfxStats = {
  cssW: 0,
  cssH: 0,
  drawW: 0,
  drawH: 0,
  scale: 1,
  aa: false,
  post: false,
  tier: 'pc',
  gpuMs: -1,
  aaMode: 'none',
  renderScale: 1,
  precompiling: false,
  cpuMs: -1,
  draws: 0,
};

/**
 * `?name=value` reader that is safe on a non-browser host (SSR/worker/test), where
 * `window.location` doesn't exist. Shared by the render modules so every graphics escape
 * hatch is read the same way: `?quality`, `?aa`, `?lights`, `?tex`, `?ibl`, `?noise`,
 * `?csm=0`, `?bloom=0`, `?post=0` and `?webgl=1` (force the WebGL2 fallback).
 */
export function queryParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/** `?name=0` — a diagnostic kill switch for one piece of scene setup. */
export function queryDisabled(name: string): boolean {
  if (queryParam(name) === '0') return true;
  // Settings → Rendering mirrors the same switches so a player can bisect a rendering bug
  // without editing the URL (Chris, 2026-09-04). Only the five the panel exposes.
  // (`taa` and `gtao` were dropped with TAAU and GTAO; ui/settings.ts may still show the
  // toggles until its owner removes them, but nothing reads them any more.)
  const s = loadSettings() as unknown as Record<string, unknown>;
  return (name === 'csm' || name === 'noise' || name === 'bloom') && s[name] === false;
}

/**
 * Scene-feature kill switches, resolved once at module load. Every one of these exists because
 * a shader-compile stall on the WebGPU backend is invisible from JS — the tab simply stops
 * responding while D3D12/DXC works through whatever node graph was just built — so the only way
 * to attribute a stall to a feature is to be able to switch that feature off from the URL and
 * re-measure.
 *
 *   ?tex=0     skip every generated-texture colorNode (materials stay flat-shaded)
 *   ?ibl=0     skip the sky PMREM bake (the hemisphere light alone carries the ambient)
 *   ?noise=0   skip the `mx_noise_float` procedural graphs on the ground and the terrain
 *   ?region=0  skip the Spine's Blender region GLB (render/region.ts): the heightfield mesh and
 *              greybox landmark boxes draw instead. Visual only — the sim still walks the
 *              sidecar's heights, which the server has too.
 *
 * Lives here rather than in `scene.ts` so `terrain.ts` can read it without importing the
 * module that imports it.
 */
export const renderFlags = {
  textures: !queryDisabled('tex'),
  ibl: !queryDisabled('ibl'),
  noise: !queryDisabled('noise'),
  region: !queryDisabled('region'),
} as const;

// --- lighting model -----------------------------------------------------------------
/**
 * Which lighting model is installed, so `scene.ts` can size its interior light budget to it.
 *
 * Always `'forward'` now. Forward+ clustered shading (`render/clustered.ts`) is deleted along
 * with the PBR path it existed for: the stylised material in `render/materials.ts` is a
 * `MeshBasicNodeMaterial` and is not lit by the lighting system at all, so a cluster grid
 * would be a per-frame compute dispatch feeding shaders that never read it. The union keeps
 * its `'clustered'` member so `scene.ts`'s `interiorLightBudget()` — owned elsewhere — still
 * type-checks; it simply always takes the forward branch, i.e. the smaller light budget,
 * which is the safe direction.
 */
export const rendererCaps: { lighting: 'forward' | 'clustered' } = { lighting: 'forward' };

/**
 * The live animation-loop callback per renderer, recorded by the `setAnimationLoop` wrapper in
 * `createRenderer` so `precompileScene` can pause and restore the loop. The *unwrapped*
 * callback is stored, so restoring it re-enters the wrapper exactly once.
 */
const animationLoops = new WeakMap<WebGPURenderer, AnimationLoopCallback>();
type AnimationLoopCallback = NonNullable<Parameters<WebGPURenderer['setAnimationLoop']>[0]>;

/**
 * Tail of the precompile queue per renderer. A deploy and the server's `welcome` can both swap
 * the map within a few seconds of each other, and two `compileAsync` calls in flight at once
 * would trample each other's renderer state (and the second would find the loop already parked,
 * so it would never restore it). Chaining keeps them strictly one at a time.
 */
const precompileQueues = new WeakMap<WebGPURenderer, Promise<unknown>>();

/**
 * Awaits every render pipeline in `scene` before the caller shows anything interactive.
 *
 * This matters more than it looks. `WebGPUPipelineUtils.createRenderPipeline` only takes
 * `device.createRenderPipelineAsync` when `compileAsync()` hands it a promises array — a plain
 * `render()` calls `device.createRenderPipeline`, which blocks the main thread through the
 * whole D3D12/DXC compile. Paying that here, behind the loading screen, is the difference
 * between a slow load and an unresponsive tab.
 *
 * The animation loop is stopped for the duration. `compileAsync` parks renderer-wide state
 * across its `await` — `_currentRenderContext`, `_compilationPromises`, `_handleObjectFunction`
 * — and shares the scene's `RenderList` with the render path, so it is only safe with nothing
 * else rendering. The frame it was showing stays on screen, and the main thread stays free:
 * the point is that input and UI keep running, not that the picture keeps moving.
 *
 * Caveat, measured: only the WebGPU backend has an async pipeline path. On the WebGL2 fallback
 * this still blocks — a 21-light forward variant froze the tab past a 45 s CDP timeout. It is
 * called there anyway, because it at least moves that stall to the loading screen instead of
 * the first frame that happens to draw a new material mid-raid. The real mitigation for that
 * backend is the small forward light budget in `scene.ts`.
 */
export function precompileScene(
  renderer: WebGPURenderer,
  scene: Object3D,
  camera: Camera,
  label: string,
  extra?: () => Promise<void>,
): Promise<number> {
  const queued = (precompileQueues.get(renderer) ?? Promise.resolve()).then(() =>
    runPrecompile(renderer, scene, camera, label, extra),
  );
  // The queue only sequences; a rejection is already swallowed inside `runPrecompile`, and
  // `catch` here keeps a future failure from poisoning every later precompile.
  precompileQueues.set(
    renderer,
    queued.catch(() => undefined),
  );
  return queued;
}

async function runPrecompile(
  renderer: WebGPURenderer,
  scene: Object3D,
  camera: Camera,
  label: string,
  extra?: () => Promise<void>,
): Promise<number> {
  const t = performance.now();
  const loop = animationLoops.get(renderer) ?? null;
  try {
    if (loop !== null) await renderer.setAnimationLoop(null);
    gfxStats.precompiling = true;
    for (const h of precompileHooks) h('begin');
    for (const w of warmPools) w.scene.add(w.pool);
    // The world scene has `matrixWorldAutoUpdate` off (render/scene.ts) and `compileAsync`
    // honours that flag, so anything attached since the last frame — the pools above, a late
    // region — gets its world matrix here.
    scene.updateMatrixWorld();
    // With the post chain on, the world never draws to the canvas — only the chain's output
    // quad does — so a canvas compile builds pipelines no frame uses (4.5 s of the cold deploy,
    // measured 2026-09-05). `extra` is the chain's own compile in that case; see render/post.ts.
    if (!gfxStats.post) await renderer.compileAsync(scene, camera);
    // Anything else that has to compile with the render loop parked — with the post chain on,
    // `render/post.ts` recompiles the same materials against its pass's render target, which
    // is a different pipeline key from the canvas compile above. Runs inside the same parked
    // window because `compileAsync` parks renderer-wide state and is only safe with nothing
    // else rendering.
    if (extra !== undefined) await extra();
  } catch (err) {
    // A failed precompile costs latency on the first frame, never correctness — the render
    // path compiles what it needs anyway.
    console.warn(`[stillsun] shader precompile failed (${label}); compiling on demand`, err);
  } finally {
    // Always restore the loop, including on a thrown compile — a raid that never draws again
    // would be a far worse failure than the stall this exists to avoid.
    for (const h of precompileHooks) {
      try {
        h('end');
      } catch (err) {
        console.warn('[stillsun] precompile hook threw', err);
      }
    }
    for (const w of warmPools) w.scene.remove(w.pool);
    gfxStats.precompiling = false;
    if (loop !== null) void renderer.setAnimationLoop(loop);
  }
  const ms = performance.now() - t;
  console.info(`[stillsun] shaders compiled in ${Math.round(ms)} ms · ${label}`);
  return ms;
}

/**
 * `?quality=low|high|ultra` forces a tier — the only way to test the low tier on a fast
 * machine (and the escape hatch when the heuristic gets a device wrong).
 *
 * Desktop only. The user-agent/touch-points phone sniff is gone: STILLSUN is a third-person
 * MMORPG with a 300-character crowd budget and no phone build to resolve to, and the sniff
 * was also demoting every touchscreen laptop and every Windows tablet-mode desktop to the
 * lowest tier on the strength of `maxTouchPoints > 0`.
 */
export function resolveQuality(): QualityTier {
  try {
    const q = new URLSearchParams(window.location.search).get('quality');
    if (q === 'low') return 'low';
    if (q === 'high') return 'pc';
    if (q === 'ultra') return 'ultra';
  } catch {
    /* no location (SSR/worker) — fall through to the heuristic */
  }
  // Settings pick, chosen in the hub. Takes effect on the next reload, which is what the
  // Settings tab tells the player: the tier decides shadow map size and pixel ratio at
  // renderer construction and cannot be swapped underneath a live scene.
  const choice = loadSettings().quality;
  if (choice === 'low') return 'low';
  if (choice === 'high') return 'pc';
  if (choice === 'ultra') return 'ultra';
  // Low-end heuristic, and the only one left: a machine reporting very few logical cores is
  // the one that cannot afford the material detail layers either.
  try {
    if ((navigator.hardwareConcurrency || 8) <= 4) return 'low';
  } catch {
    /* ignore */
  }
  return 'pc';
}

/**
 * Hard cap on the canvas backing store's long edge. The reported "slideshow" stutter
 * traced to a 4080 laptop at devicePixelRatio 1.5 driving a 3828x1911 (7.3 MP) MSAA
 * target — most of the frame cost on its own. 2560 keeps the long edge at a QHD-ish
 * ceiling no matter how dense or how zoomed-out the display is.
 */
const RES_CAP_LONG_EDGE_PX = 2560;

/**
 * Per-tier ceiling on the device pixel ratio. The standard tier renders at CSS pixels: on the
 * owner's 4080 a 1.25 dpr window produced a 2472x1901 backing store (4.7 MP) and 10-15 fps
 * (2026-09-05); ultra may still go to 2, which is the one place the two tiers still differ in
 * cost rather than in optional features. The low tier keeps 1.25 for text crispness.
 *
 * This is a *pixel-ratio* ceiling, not a resolution one: the 2560 long-edge cap above applies
 * to every tier now. The old 1920 belt on the standard tier was there because TAAU's resolve,
 * GTAO, the weapon's MSAA pass, bloom and the shafts all ran at output resolution outside DRS.
 * Every one of those passes is gone (render/post.ts) and PERF-BUDGET's gate row is 2560x1440,
 * so a cap below the gate would have made the budget unmeasurable on the machine it is written
 * for.
 */
const tierPixelRatioCap = (tier: QualityTier): number =>
  tier === 'low' ? 1.25 : tier === 'ultra' ? 2 : 1;

/**
 * pixelRatio = min(devicePixelRatio, RES_CAP_LONG_EDGE_PX / max(cssW, cssH), tierCap)
 *
 * The middle term is what actually bounds the backing store: at cssW=2552/cssH=1274
 * (a 3828x1911 canvas at dpr 1.5) it evaluates to 2560/2552 ~= 1.0, i.e. dpr 1.5 gets
 * clamped down to ~1.0 and the backing store lands at ~2552x1274 instead of 3828x1911.
 */
function basePixelRatio(tier: QualityTier, cssW: number, cssH: number): number {
  const dpr = window.devicePixelRatio || 1;
  // ?rescap=N lifts/lowers the long-edge cap for a measurement run (e.g. native 2560x1440 on pc).
  const capOverride = Number.parseInt(queryParam('rescap') ?? '', 10);
  const capPx =
    Number.isFinite(capOverride) && capOverride > 0 ? capOverride : RES_CAP_LONG_EDGE_PX;
  const longEdgeCap = capPx / Math.max(cssW, cssH, 1);
  return Math.min(dpr, longEdgeCap, tierPixelRatioCap(tier));
}

/** `?aa=0|1` forces MSAA off/on regardless of tier or resolution. */
function resolveAAOverride(): boolean | null {
  try {
    const q = new URLSearchParams(window.location.search).get('aa');
    if (q === '0') return false;
    if (q === '1') return true;
  } catch {
    /* no location (SSR/worker) */
  }
  return null;
}

/** MSAA is only worth its bandwidth up to a "1440p tall" backing store; past that the
 * resolve cost dominates and it comes off entirely. */
const AA_MAX_DRAW_HEIGHT_PX = 1440;

// --- dynamic resolution scaling ----------------------------------------------------
/** How often the EMA is sampled and resScale is allowed to step. */
const DRS_INTERVAL_MS = 500;
/** EMA above this (ms) => step resScale down; below this => step up. Deliberately not
 * adjacent (17 vs 11) so a steady ~13-16ms frame neither climbs nor falls — no thrash
 * right at the boundary. */
// Frames are vsync-capped at ~16.7 ms, so a healthy 60 fps frame never reads below 11 ms and
// the old 11 ms "step up" rule could never fire once the scale had dropped. Step up whenever
// the EMA sits at/under the vsync period; step down only on a clear miss.
const DRS_HIGH_MS = 18.5;
const DRS_LOW_MS = 16.9;
const DRS_DOWN_STEP = 0.08;
const DRS_UP_STEP = 0.05;
// 0.65 with the 0.75 sink floor keeps the *canvas* ratio at or above 0.87: the floor at 0.55 put
// the canvas at 0.73x and read as "the graphics look worse" on a frame DRS could not rescue anyway.
const DRS_MIN_SCALE = 0.65;
/** With a DRS sink, the scene pass takes the first cut down to this; below it the canvas ratio
 * drops too (see applyPixelRatio). Unused while no sink is registered. */
const DRS_SINK_FLOOR = 0.75;
const DRS_MAX_SCALE = 1;
/** setPixelRatio is a GPU resource rebuild; only pay for it once the change is visible. */
const DRS_APPLY_EPSILON = 0.02;
/** Exponential-smoothing factor for the frame-time EMA — roughly an 8-frame half-life,
 * enough to ride out a single GC hitch without over- or under-reacting to it. */
const EMA_ALPHA = 0.12;
/** A backgrounded tab (or a debugger pause) can report a multi-second gap on resume;
 * without a clamp that one sample would yank the EMA (and resScale) off a cliff. */
const DRS_MAX_SAMPLE_MS = 250;
/**
 * A frame whose main-thread time is at least this share of the rAF delta is CPU-bound: fewer
 * pixels cannot help it, so DRS climbs back instead of sinking. Measured case (2026-09-05,
 * owner's 4080): 80-97 ms frames with the GPU at 36%.
 */
const CPU_BOUND_FRACTION = 0.8;

/**
 * **The governor's quality latch is gone and is not coming back.** It used to watch for a frame
 * EMA above 28 ms with DRS already at its floor and then persist `quality: low` into
 * `localStorage`. On the owner's 4080 (2026-09-05) that fired on a frame that was *main-thread*
 * bound with the GPU 36% busy: it made nothing faster, it made the next load worse, and it
 * needed a `qualityBy` provenance field and a one-shot un-latch in `ui/settings.ts` to undo
 * itself on every profile it had already hit. DRS climbs back out of a bad frame; a latch does
 * not. See PERF-BUDGET rule 7.
 *
 * The hook and its event type stay exported because `main.ts` (owned elsewhere) still
 * subscribes. Nothing ever fires them.
 */
export interface GovernorEvent {
  from: QualityTier;
  to: 'low' | 'high';
  emaMs: number;
}
export type GovernorListener = (ev: GovernorEvent) => void;
const governorListeners = new Set<GovernorListener>();
/** @deprecated Never fires — see `GovernorEvent`. Kept so `main.ts` keeps compiling. */
export function onGovernor(fn: GovernorListener): () => void {
  governorListeners.add(fn);
  return () => {
    governorListeners.delete(fn);
  };
}

/**
 * Boots a WebGPURenderer against the given canvas. WebGPURenderer transparently
 * falls back to a WebGL2 backend, so this resolves on any reasonably modern browser.
 */
export async function createRenderer(canvas: HTMLCanvasElement): Promise<RendererHandle> {
  // Feature-detect rather than letting the renderer probe-and-fail: on browsers
  // without navigator.gpu the probe throws noisily into the console.
  // `?webgl=1` forces the WebGL2 fallback on a WebGPU machine — the only way to exercise (and
  // measure) the fallback path without finding hardware that lacks WebGPU.
  const hasWebGPU =
    typeof navigator !== 'undefined' && 'gpu' in navigator && queryParam('webgl') !== '1';
  const tier = resolveQuality();

  const cssW0 = Math.max(1, window.innerWidth);
  const cssH0 = Math.max(1, window.innerHeight);
  const base0 = basePixelRatio(tier, cssW0, cssH0);
  const aaOverride = resolveAAOverride();
  // With the post chain on, the scene renders into the chain's own 4x-MSAA pass target and the
  // canvas only ever receives the output quad, so canvas MSAA would resolve a second time for
  // nothing. This branch is therefore the `?post=0` path: MSAA survives only under the
  // 1440p-tall backing-store ceiling, above which the resolve cost is most of what made this
  // a "slideshow" in the first place, and the low tier skips it outright.
  const postOn = new URLSearchParams(window.location.search).get('post') !== '0';
  const antialias =
    aaOverride ??
    (postOn ? false : tier !== 'low' ? cssH0 * base0 <= AA_MAX_DRAW_HEIGHT_PX : false);

  const params: WebGPURendererParameters = { canvas, antialias };
  if (!hasWebGPU) params.forceWebGL = true;
  // `?gputime=1`: WebGPU timestamp queries, resolved every few frames into `gfxStats.gpuMs`.
  // Off by default — the query pool costs a little per pass — and a no-op on WebGL2.
  const wantsGpuTime = queryParam('gputime') === '1';
  if (wantsGpuTime && hasWebGPU) params.trackTimestamp = true;

  const renderer = new WebGPURenderer(params);
  renderer.toneMapping = AgXToneMapping;
  // Eternal dusk: the Preetham sky is the brightest thing on screen, so exposure is
  // set for the sky and the analytic lights are scaled up to match (see scene.ts).
  // Raised 0.55 -> 0.72 alongside the scene.ts fill-light lift: at 0.55 anything the
  // sun's grazing 4.5deg throw missed read as pure black instead of dark amber shadow.
  renderer.toneMappingExposure = 0.72;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  // PCFSoftShadowMap is deprecated for the WebGPU path in r181+.
  renderer.shadowMap.type = PCFShadowMap;

  // Forward lighting, always. Forward+ clustered shading (`render/clustered.ts`, deleted) was
  // the answer to "26 lamps unroll into every material's shader and each set is its own cache
  // key". The stylised path does not have that problem to solve: characters and gear are
  // unlit `MeshBasicNodeMaterial`s (render/materials.ts) that the lighting system never
  // touches, so the surviving lit surfaces are a handful of world materials against a light
  // budget `scene.ts` already sizes for forward.

  await renderer.init();

  // `isWebGPUBackend` only exists on the concrete WebGPUBackend subclass.
  const backendFlag = (renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend === true;
  const backend: 'webgpu' | 'webgl2' = backendFlag ? 'webgpu' : 'webgl2';

  gfxStats.tier = tier;
  // Which adapter WebGPU actually handed over. A dual-GPU laptop can answer with the
  // integrated part; the line exists so that shows up in the first console screenshot.
  let gpuName = '';
  if (backend === 'webgpu') {
    try {
      const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
      const adapter = (await gpu?.requestAdapter()) as { info?: Record<string, unknown> } | null;
      const info = adapter?.info;
      if (info) {
        gpuName = ['vendor', 'architecture', 'device', 'description']
          .map((k) => info[k])
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
          .join(' ');
      }
    } catch {
      /* adapter info is optional */
    }
  }
  console.info(
    `[stillsun] renderer backend: ${backend}${gpuName ? ` (${gpuName})` : ''} · quality tier: ${tier} · aa: ${String(antialias)} · lighting: ${rendererCaps.lighting}` +
      `${wantsGpuTime ? ` · gpu timestamps: ${backend === 'webgpu' ? 'on' : 'unavailable'}` : ''}`,
  );

  // --- GPU timing ------------------------------------------------------------------------
  // `resolveTimestampsAsync` maps the query buffer back. It has to run every frame: three's
  // query pool is sized for one frame of passes and warns (then stops recording) when it is
  // not drained. The readback is async, so a frame never waits on it; at most one in flight.
  let gpuTimePending = false;
  const sampleGpuTime = (): void => {
    if (!wantsGpuTime || backend !== 'webgpu') return;
    if (gpuTimePending) return;
    gpuTimePending = true;
    void renderer
      .resolveTimestampsAsync('render')
      .then((ms) => {
        if (typeof ms === 'number' && Number.isFinite(ms)) gfxStats.gpuMs = ms;
        else gfxStats.gpuMs = renderer.info.render.timestamp;
      })
      .catch(() => {
        gfxStats.gpuMs = -1;
      })
      .finally(() => {
        gpuTimePending = false;
      });
  };

  const camera = new PerspectiveCamera(75, 1, 0.05, 1000);
  camera.rotation.order = 'YXZ';
  camera.layers.enable(RIG_LAYER);

  const listeners: Array<(aspect: number) => void> = [];

  // --- resolution: hard cap (base) with dynamic scaling (resScale) layered on top ---
  let base = base0;
  let resScale = 1;
  let lastAppliedRatio = -1;

  const applyPixelRatio = (): void => {
    // No sink is registered today, so this is the plain path: DRS moves the canvas pixel ratio
    // and the whole post chain scales with it. The hybrid branch below survives for a future
    // upscaler — it takes the first step down out of the scene pass alone, but only to
    // DRS_SINK_FLOOR, because anything running at *output* resolution does not get cheaper
    // when the scene pass shrinks. Measured on the owner's 4080 at 2472x1901 (2026-09-05):
    // scene-only DRS bottomed out at 0.55x with the frame still at 55 ms.
    let ratio = base * resScale;
    if (drsSink !== null) {
      const sceneScale = Math.max(DRS_SINK_FLOOR, resScale);
      ratio = base * (resScale / sceneScale);
      drsSink(sceneScale);
    }
    if (Math.abs(ratio - lastAppliedRatio) >= DRS_APPLY_EPSILON) {
      lastAppliedRatio = ratio;
      renderer.setPixelRatio(ratio);
    }
    gfxStats.scale = resScale;
    gfxStats.drawW = Math.round(gfxStats.cssW * ratio);
    gfxStats.drawH = Math.round(gfxStats.cssH * ratio);
  };

  const resize = (): void => {
    const w = Math.max(1, window.innerWidth);
    const h = Math.max(1, window.innerHeight);
    gfxStats.cssW = w;
    gfxStats.cssH = h;
    base = basePixelRatio(tier, w, h);
    applyPixelRatio();
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    for (const fn of listeners) {
      try {
        fn(w / h);
      } catch (err) {
        console.warn('[stillsun] resize listener threw', err);
      }
    }
  };
  gfxStats.aa = antialias;
  resize();

  window.addEventListener('resize', resize, { passive: true });
  window.addEventListener('orientationchange', resize, { passive: true });

  // --- dynamic resolution scaling ----------------------------------------------------
  // main.ts owns the render loop and calls `renderer.setAnimationLoop(frame)` directly
  // on this instance, so frame timing is captured here by wrapping that method rather
  // than by threading a per-frame dt callback through main.ts.
  const originalSetAnimationLoop = renderer.setAnimationLoop.bind(renderer);
  let lastFrameTime = -1;
  let emaMs = -1;
  let cpuEmaMs = -1;
  let accumMs = 0;

  const sampleDrs = (): void => {
    const cpuBound = cpuEmaMs >= 0 && cpuEmaMs >= emaMs * CPU_BOUND_FRACTION;
    if (cpuBound) {
      // Pixels are not the problem; give them back.
      resScale = Math.min(DRS_MAX_SCALE, resScale + DRS_UP_STEP);
    } else if (emaMs > DRS_HIGH_MS) {
      resScale = Math.max(DRS_MIN_SCALE, resScale - DRS_DOWN_STEP);
    } else if (emaMs < DRS_LOW_MS) {
      resScale = Math.min(DRS_MAX_SCALE, resScale + DRS_UP_STEP);
    }
    applyPixelRatio();
    // Nothing follows. The quality latch that used to live here — strikes, a localStorage
    // write and a `governorListeners` fan-out — is deleted; see `GovernorEvent` above.
  };

  renderer.setAnimationLoop = (callback: AnimationLoopCallback | null): Promise<void> => {
    if (callback === null) {
      animationLoops.delete(renderer);
      return originalSetAnimationLoop(null);
    }
    // The unwrapped callback, so `precompileScene` can stop and restart the loop without
    // stacking a second timing wrapper on every pause.
    animationLoops.set(renderer, callback);
    // A pause leaves an arbitrarily long gap; drop the stale timestamp so the resumed loop
    // doesn't feed one huge sample into the resolution-scaling EMA.
    lastFrameTime = -1;
    const wrapped: AnimationLoopCallback = (time, frame) => {
      if (lastFrameTime >= 0) {
        const dtMs = Math.min(time - lastFrameTime, DRS_MAX_SAMPLE_MS);
        emaMs = emaMs < 0 ? dtMs : emaMs + (dtMs - emaMs) * EMA_ALPHA;
        accumMs += dtMs;
        if (accumMs >= DRS_INTERVAL_MS) {
          accumMs = 0;
          sampleDrs();
        }
      }
      lastFrameTime = time;
      for (const hook of frameHooks) {
        try {
          hook(time);
        } catch (err) {
          console.warn('[stillsun] frame hook threw', err);
        }
      }
      const t0 = performance.now();
      callback(time, frame);
      const cpuMs = performance.now() - t0;
      cpuEmaMs = cpuEmaMs < 0 ? cpuMs : cpuEmaMs + (cpuMs - cpuEmaMs) * EMA_ALPHA;
      gfxStats.cpuMs = cpuEmaMs;
      gfxStats.draws = renderer.info.render.drawCalls;
      sampleGpuTime();
    };
    return originalSetAnimationLoop(wrapped);
  };

  // Reachable straight off the renderer instance too, per the perf brief — same object
  // as the `gfxStats` export, so either path sees live updates.
  (renderer as unknown as { stats: GfxStats }).stats = gfxStats;
  // For tools/scripts/perf-probe.mjs: it pins the camera to fixed poses for repeatable
  // before/after screenshots by wrapping `updateMatrixWorld` on this exact object.
  try {
    (globalThis as { __stillsunCamera?: PerspectiveCamera }).__stillsunCamera = camera;
    (globalThis as { __stillsunGfx?: GfxStats }).__stillsunGfx = gfxStats;
  } catch {
    /* non-browser host */
  }

  return {
    renderer,
    camera,
    backend,
    tier,
    onResize(fn) {
      listeners.push(fn);
      fn(camera.aspect);
    },
    dispose() {
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
      renderer.setAnimationLoop(null);
      renderer.dispose();
    },
  };
}
